import { useRetentionDefaults } from "./hooks/useRetentionDefaults";
import {
  mergeRetentionsWithDefaults,
  addRetentionMetadata,
  addBlankRowIfNeeded,
} from "./utils/retentionUtils";
import {
  MoreVert as MoreIcon,
  SettingsBackupRestore as ResetIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import NotesIcon from "@mui/icons-material/Notes";
import {
  Autocomplete,
  IconButton as MuiIconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Alert,
} from "@mui/material";
import useElementSize from "common/hooks/useElementSize";
import * as ids from "common/ids";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Notes from "components/common/Notes";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { selectSelfInsuredRetentions } from "store/selectors/input/claims/claimsSelectors";

const NOTES_STACK_BREAKPOINT = 1050;

const ActionMenu = ({ onResetRetentions }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const resetRetentions = async () => {
    await onResetRetentions();
    setIsOpen(false);
  };

  return (
    <div className={"menu"}>
      <MuiIconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </MuiIconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem onClick={resetRetentions}>
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabel(labels.RETENTIONS_RESET_TO_DEFAULTS, {
              default: "Reset to defaults",
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const RetentionTypeAutocomplete = React.memo(
  function RetentionTypeAutocomplete({ options, value, onChange, ...props }) {
    const selectedOption =
      (options || []).find((opt) => opt.value === value) || null;

    return (
      <Autocomplete
        options={options || []}
        value={selectedOption}
        onChange={(event, newValue) => {
          onChange({ target: { value: newValue?.value || "" } });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={"filled"}
            size={"small"}
            fullWidth
            hiddenLabel
            placeholder={"Select retention type..."}
          />
        )}
        renderOption={(props, option) => <li {...props}>{option.label}</li>}
        {...props}
      />
    );
  }
);

const CoverageAutocomplete = React.memo(function CoverageAutocomplete({
  options,
  value,
  onChange,
  ...props
}) {
  const selectedOption =
    (options || []).find((opt) => opt.value === value) || null;

  return (
    <Autocomplete
      options={options || []}
      value={selectedOption}
      onChange={(event, newValue) => {
        onChange({ target: { value: newValue?.value || "" } });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"filled"}
          size={"small"}
          fullWidth
          hiddenLabel
          placeholder={"Select coverage..."}
        />
      )}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
      {...props}
    />
  );
});

const RetentionRow = React.memo(function RetentionRow({
  stableKey,
  retentionId,
  isBlankRow,
  originalIndex,
  retentionType,
  retentionCoverageCategory,
  retentionKey,
  origin,
  userModified,
  lastDefaultValue,
  defaultType,
  defaultCoverage,
  importedFromLossType,
  value,
  notes,
  canDelete,
  retentionTypeOptions,
  coverageTypeOptions,
  stackNotes,
  isOpen,
  totalColumns,
  onToggleOpen,
  onDelete,
  onUpdate,
}) {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return (
    <React.Fragment>
      <TableRow
        sx={{
          opacity: isBlankRow ? 0.7 : 1,
          "&:hover": {
            opacity: 1,
          },
        }}
      >
        <TableCell>
          {(() => {
            const typeMismatch =
              origin === "default" &&
              !!defaultType &&
              !!retentionType &&
              retentionType !== defaultType;
            const defaultTypeLabel =
              (retentionTypeOptions || []).find((o) => o.value === defaultType)
                ?.label ||
              defaultType ||
              "Unknown";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <RetentionTypeAutocomplete
                    value={retentionType || ""}
                    options={retentionTypeOptions}
                    onChange={(e) =>
                      onUpdate(
                        {
                          id: retentionId,
                          isBlank: isBlankRow,
                          originalIndex,
                          retentionType,
                          retentionCoverageCategory,
                          value,
                          notes,
                        },
                        "retentionType",
                        e.target.value
                      )
                    }
                  />
                </div>
                {typeMismatch && (
                  <Tooltip title={`Default: ${defaultTypeLabel}`}>
                    <InfoOutlinedIcon fontSize={"small"} color={"primary"} />
                  </Tooltip>
                )}
                {origin === "imported" && importedFromLossType && (
                  <Tooltip
                    title={`Imported (lossType: ${importedFromLossType})`}
                  >
                    <MoveToInboxIcon fontSize={"small"} color={"secondary"} />
                  </Tooltip>
                )}
              </div>
            );
          })()}
        </TableCell>
        <TableCell>
          {(() => {
            const coverageMismatch =
              origin === "default" &&
              !!defaultCoverage &&
              !!retentionCoverageCategory &&
              retentionCoverageCategory !== defaultCoverage;
            const defaultCoverageLabel =
              (coverageTypeOptions || []).find(
                (o) => o.value === defaultCoverage
              )?.label ||
              defaultCoverage ||
              "Unknown";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <CoverageAutocomplete
                    value={retentionCoverageCategory || ""}
                    options={coverageTypeOptions}
                    onChange={(e) =>
                      onUpdate(
                        {
                          id: retentionId,
                          isBlank: isBlankRow,
                          originalIndex,
                          retentionType,
                          retentionCoverageCategory,
                          value,
                          notes,
                        },
                        "retentionCoverageCategory",
                        e.target.value
                      )
                    }
                  />
                </div>
                {coverageMismatch && (
                  <Tooltip title={`Default: ${defaultCoverageLabel}`}>
                    <InfoOutlinedIcon fontSize={"small"} color={"primary"} />
                  </Tooltip>
                )}
              </div>
            );
          })()}
        </TableCell>
        <TableCell align={"right"}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ flex: 1 }}>
              <PrettyNumberTextField
                prefix={"$"}
                onChangeNumber={(v) =>
                  onUpdate(
                    {
                      id: retentionId,
                      isBlank: isBlankRow,
                      originalIndex,
                      retentionType,
                      retentionCoverageCategory,
                      value,
                      notes,
                    },
                    "value",
                    v
                  )
                }
                value={value}
                size={"small"}
                fullWidth
                hiddenLabel
              />
            </div>
            {origin === "default" && userModified && lastDefaultValue != null && (
              <Tooltip
                title={`Default: $${numbers.abbreviated(lastDefaultValue)}`}
              >
                <InfoOutlinedIcon fontSize={"small"} color={"primary"} />
              </Tooltip>
            )}
          </div>
        </TableCell>
        {!stackNotes && (
          <TableCell>
            <Notes
              title={null}
              update={(comment) =>
                onUpdate(
                  {
                    id: retentionId,
                    isBlank: isBlankRow,
                    originalIndex,
                    retentionType,
                    retentionCoverageCategory,
                    value,
                    notes,
                  },
                  "notes",
                  comment || null
                )
              }
              value={notes || ""}
              hasEditButton={false}
            />
          </TableCell>
        )}
        <TableCell>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              minWidth: "fit-content",
            }}
          >
            {stackNotes && (
              <Tooltip
                title={translateLabel(
                  isOpen
                    ? labels.CLAIMS_TABLE_HIDE_NOTES
                    : labels.CLAIMS_TABLE_SHOW_NOTES,
                  {
                    default: isOpen ? "Hide Notes" : "Show Notes",
                  }
                )}
              >
                <MuiIconButton
                  onClick={() => onToggleOpen(stableKey)}
                  size={"small"}
                  aria-label={translateLabel(
                    isOpen
                      ? labels.CLAIMS_TABLE_HIDE_NOTES
                      : labels.CLAIMS_TABLE_SHOW_NOTES,
                    {
                      default: isOpen ? "Hide Notes" : "Show Notes",
                    }
                  )}
                  sx={{
                    backgroundColor: isOpen ? "action.selected" : "transparent",
                    "&:hover": {
                      backgroundColor: isOpen ? "action.hover" : "action.hover",
                    },
                  }}
                >
                  <NotesIcon
                    fontSize={"small"}
                    color={
                      notes && notes.trim()
                        ? "primary"
                        : isOpen
                        ? "primary"
                        : "action"
                    }
                  />
                </MuiIconButton>
              </Tooltip>
            )}
            {canDelete && !isBlankRow && (
              <MuiIconButton
                onClick={() => onDelete(retentionId)}
                size={"small"}
              >
                <DeleteIcon />
              </MuiIconButton>
            )}
          </div>
        </TableCell>
      </TableRow>
      {stackNotes && isOpen && (
        <TableRow>
          <TableCell colSpan={totalColumns}>
            <Notes
              title={null}
              update={(comment) =>
                onUpdate(
                  {
                    id: retentionId,
                    isBlank: isBlankRow,
                    originalIndex,
                    retentionType,
                    retentionCoverageCategory,
                    value,
                    notes,
                  },
                  "notes",
                  comment || null
                )
              }
              value={notes || ""}
              hasEditButton={false}
            />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
});

const ProLinesRetentions = React.memo(({ componentProps }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const dispatch = useDispatch();

  const {
    getApplicableDefaults,
    productLine,
    retentionsConfig,
  } = useRetentionDefaults();

  const selfInsuredRetentions = useSelector(selectSelfInsuredRetentions);

  const setSelfInsuredRetentions = useCallback(
    (retentions) =>
      dispatch(claimsActions.setSelfInsuredRetentions(retentions)),
    [dispatch]
  );
  const deleteSelfInsuredRetention = useCallback(
    (id) => dispatch(claimsActions.deleteSelfInsuredRetention(id)),
    [dispatch]
  );
  const updateSelfInsuredRetention = useCallback(
    (retention) =>
      dispatch(claimsActions.updateSelfInsuredRetention(retention)),
    [dispatch]
  );

  const retentionTypeOptions = useMemo(() => {
    return (retentionsConfig?.retentionTypes || []).map((t) => ({
      label: t.display,
      value: t.key,
    }));
  }, [retentionsConfig?.retentionTypes]);

  const coverageTypeOptions = useMemo(() => {
    const baseOptions = retentionsConfig?.retentionCoverageCategory || [];
    return baseOptions.map((c) => ({ label: c.display, value: c.key }));
  }, [retentionsConfig?.retentionCoverageCategory]);

  const previousProductLine = useRef(productLine);
  const selfInsuredRetentionsRef = useRef(selfInsuredRetentions);
  selfInsuredRetentionsRef.current = selfInsuredRetentions;

  const blankRowIdRef = useRef(null);
  const getUniqueBlankId = useCallback((retentions) => {
    const existingIds = new Set((retentions || []).map((r) => String(r?.id)));
    let candidate = blankRowIdRef.current ?? ids.makeNewId();
    while (existingIds.has(String(candidate))) {
      candidate = ids.makeNewId();
      blankRowIdRef.current = candidate;
    }
    blankRowIdRef.current = candidate;
    return candidate;
  }, []);

  // Determine if current retentions (default-derived subset) match current product line defaults
  const applicableDefaultsMemo = useMemo(() => getApplicableDefaults(), [
    getApplicableDefaults,
  ]);

  // Helper to derive a logical key when one isn't present
  const deriveKey = useCallback((r) => {
    if (!r) return undefined;
    return (
      r.retentionKey ||
      (r.retentionType && (r.retentionCoverageCategory || r.lossType)
        ? `${r.retentionType}-${r.retentionCoverageCategory || r.lossType}`
        : undefined)
    );
  }, []);

  // Index defaults by their key (or derived key) for quick lookup per row
  const defaultsByKey = useMemo(() => {
    const map = new Map();
    (applicableDefaultsMemo || []).forEach((d) => {
      const k = d.key || deriveKey(d);
      if (k) map.set(k, d);
    });
    return map;
  }, [applicableDefaultsMemo, deriveKey]);

  const retentionsMismatchWithDefaults = useMemo(() => {
    const defaultsKeys = new Set(
      (applicableDefaultsMemo || [])
        .map((d) => d.key || deriveKey(d))
        .filter(Boolean)
    );

    if (defaultsKeys.size === 0) return false;

    // Consider only rows whose keys are in the current defaults set (default-derived subset)
    const currentKeys = new Set(
      (selfInsuredRetentions || [])
        .map((r) => deriveKey(r))
        .filter((k) => k && defaultsKeys.has(k))
    );

    // Missing any defaults?
    let missing = false;
    for (const k of defaultsKeys) {
      if (!currentKeys.has(k)) {
        missing = true;
        break;
      }
    }

    // Extra default-derived rows not in defaults? (should be covered by filter, but keep symmetric check)
    let extra = false;
    for (const k of currentKeys) {
      if (!defaultsKeys.has(k)) {
        extra = true;
        break;
      }
    }

    return missing || extra;
  }, [applicableDefaultsMemo, selfInsuredRetentions, deriveKey]);

  const handleResetToDefaults = async () => {
    const applicableDefaults = getApplicableDefaults();
    if (applicableDefaults.length === 0) return;

    const currentRetentions = selfInsuredRetentions || [];
    const mergedRetentions = mergeRetentionsWithDefaults(
      currentRetentions,
      applicableDefaults
    );

    const normalized = mergedRetentions.map((r) => ({
      ...r,
      id: r.id || ids.makeNewId(),
      retentionCoverageCategory:
        r.retentionCoverageCategory ?? r.lossType ?? "",
      retentionKey:
        r.retentionKey ||
        r.key ||
        (r.retentionType && (r.retentionCoverageCategory || r.lossType)
          ? `${r.retentionType}-${r.retentionCoverageCategory || r.lossType}`
          : undefined),
      origin: r.origin ?? "default",
      lastDefaultValue: r.hasOwnProperty("lastDefaultValue")
        ? r.lastDefaultValue
        : r.value ?? null,
      userModified: r.userModified ?? false,
    }));

    setSelfInsuredRetentions(normalized);
  };

  useEffect(() => {
    const currentDefaults = getApplicableDefaults();
    const userRetentions = selfInsuredRetentionsRef.current || [];

    const noRetentions = userRetentions.length === 0;
    const hasDefaults = currentDefaults.length > 0;

    // Only seed defaults when there are no retentions at all.
    // Do NOT reset on product line change automatically; user must click reset.
    if (noRetentions && hasDefaults) {
      const merged = mergeRetentionsWithDefaults([], currentDefaults);
      const normalizedDefaults = merged.map((r) => ({
        ...r,

        id: r.id || ids.makeNewId(),
        retentionCoverageCategory:
          r.retentionCoverageCategory ?? r.lossType ?? "",

        retentionKey:
          r.retentionKey ||
          r.key ||
          (r.retentionType && (r.retentionCoverageCategory || r.lossType)
            ? `${r.retentionType}-${r.retentionCoverageCategory || r.lossType}`
            : undefined),

        origin: r.origin ?? "default",
        lastDefaultValue: r.hasOwnProperty("lastDefaultValue")
          ? r.lastDefaultValue
          : r.value ?? null,
        userModified: r.userModified ?? false,
      }));
      setSelfInsuredRetentions(normalizedDefaults);
    }

    previousProductLine.current = productLine;
    // Intentionally omit selfInsuredRetentions from deps: effect seeds defaults only when empty.
    // Reading from ref avoids infinite loop (effect calls setSelfInsuredRetentions).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLine, getApplicableDefaults, setSelfInsuredRetentions]);

  const displayRetentions = useMemo(() => {
    const userRetentions = selfInsuredRetentions || [];
    const retentionsWithMetadata = addRetentionMetadata(userRetentions);
    const blankRowId = getUniqueBlankId(retentionsWithMetadata);
    const result = addBlankRowIfNeeded(retentionsWithMetadata, blankRowId);

    return result;
  }, [selfInsuredRetentions, getUniqueBlankId]);

  const handleRetentionUpdate = useCallback(
    (retention, field, value) => {
      if (retention.isBlank && value) {
        const applicableDefaults = getApplicableDefaults();

        let matchingDefault = null;
        if (field === "retentionType") {
          matchingDefault = applicableDefaults.find(
            (d) => d.retentionType === value
          );
        } else if (field === "retentionCoverageCategory") {
          matchingDefault = applicableDefaults.find(
            (d) => d.retentionCoverageCategory === value || d.lossType === value
          );
        }

        const newRetention = {
          id: retention.id,
          retentionType:
            field === "retentionType"
              ? value
              : matchingDefault?.retentionType || "",
          retentionCoverageCategory:
            field === "retentionCoverageCategory"
              ? value
              : matchingDefault?.retentionCoverageCategory ||
                matchingDefault?.lossType ||
                "",
          retentionKey:
            matchingDefault?.key ||
            ((field === "retentionType"
              ? value
              : matchingDefault?.retentionType) &&
            (field === "retentionCoverageCategory"
              ? value
              : matchingDefault?.retentionCoverageCategory ||
                matchingDefault?.lossType)
              ? `${
                  field === "retentionType"
                    ? value
                    : matchingDefault?.retentionType
                }-${
                  field === "retentionCoverageCategory"
                    ? value
                    : matchingDefault?.retentionCoverageCategory ||
                      matchingDefault?.lossType
                }`
              : undefined),
          origin: matchingDefault ? "default" : "user",
          lastDefaultValue: matchingDefault?.value ?? null,
          userModified: false,
          value: field === "value" ? value : matchingDefault?.value ?? null,
          notes: matchingDefault?.notes || null,
        };

        updateSelfInsuredRetention(newRetention);
      } else if (!retention.isBlank && retention.originalIndex !== undefined) {
        updateSelfInsuredRetention({ id: retention.id, [field]: value });
      }
    },
    [getApplicableDefaults, updateSelfInsuredRetention]
  );

  const [openRowIds, setOpenRowIds] = useState({});
  const onToggleOpen = useCallback((key) => {
    setOpenRowIds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);
  const [setContainerRef, { width: containerWidth }] = useElementSize();
  const stackNotes =
    containerWidth > 0 && containerWidth <= NOTES_STACK_BREAKPOINT;
  const totalColumns = stackNotes ? 4 : 5;

  const headerOptions = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <ActionMenu onResetRetentions={handleResetToDefaults} />
    </div>
  );

  return (
    <Component
      title={translateLabel(labels.SELF_INSURED_RETENTIONS_TITLE, {
        default: "Professional Lines Retentions",
      })}
      options={headerOptions}
      {...componentProps}
    >
      <Visible
        byTag={"claims.retentions.defaultsMismatchWarning"}
        defaultVisible={false}
      >
        {retentionsMismatchWithDefaults && (
          <Alert
            severity={"info"}
            action={
              <MuiIconButton onClick={handleResetToDefaults} size={"small"}>
                <ResetIcon fontSize={"small"} />
              </MuiIconButton>
            }
            sx={{ mb: 1 }}
          >
            {translateLabel(labels.RETENTIONS_DEFAULTS_MISMATCH_MESSAGE, {
              default: productLine
                ? `These retentions differ from the current defaults for ${productLine}. Use Reset to apply defaults.`
                : "These retentions differ from the current defaults. Use Reset to apply defaults.",
            })}
          </Alert>
        )}
      </Visible>
      <div ref={setContainerRef} style={{ width: "100%" }}>
        <Table>
          <colgroup>
            <col style={{ width: stackNotes ? "30%" : "20%" }} />
            <col style={{ width: stackNotes ? "30%" : "20%" }} />
            <col style={{ width: stackNotes ? "40%" : "20%" }} />
            {!stackNotes && <col style={{ width: "40%" }} />}
            <col style={{ width: "120px" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>
                {translateLabel(labels.SELF_INSURED_RETENTIONS_RETENTION_TYPE, {
                  default: "Retention Type",
                })}
              </TableCell>
              <TableCell>
                {translateLabel(labels.SELF_INSURED_RETENTIONS_COVERAGE, {
                  default: "Coverage",
                })}
              </TableCell>
              <TableCell align={"right"}>
                {translateLabel(labels.SELF_INSURED_RETENTIONS_AMOUNT, {
                  default: "Amount",
                })}
              </TableCell>
              {!stackNotes && (
                <TableCell>
                  {translateLabel(labels.SELF_INSURED_RETENTIONS_NOTES, {
                    default: "Notes",
                  })}
                </TableCell>
              )}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRetentions.map((retention, index) => {
              const stableKey = retention.id || `retention-${index}`;
              const defaultMatch = defaultsByKey.get(retention.retentionKey);
              return (
                <RetentionRow
                  key={stableKey}
                  stableKey={stableKey}
                  retentionId={retention.id}
                  isBlankRow={retention.isBlank}
                  originalIndex={retention.originalIndex}
                  retentionType={retention.retentionType || ""}
                  retentionCoverageCategory={
                    retention.retentionCoverageCategory || ""
                  }
                  retentionKey={retention.retentionKey}
                  origin={retention.origin}
                  userModified={retention.userModified}
                  lastDefaultValue={retention.lastDefaultValue}
                  defaultType={defaultMatch?.retentionType}
                  defaultCoverage={
                    defaultMatch?.retentionCoverageCategory ||
                    defaultMatch?.lossType
                  }
                  importedFromLossType={retention.importedFromLossType}
                  value={retention.value}
                  notes={retention.notes}
                  canDelete={retention.canDelete !== false}
                  retentionTypeOptions={retentionTypeOptions}
                  coverageTypeOptions={coverageTypeOptions}
                  stackNotes={stackNotes}
                  isOpen={!!openRowIds[stableKey]}
                  totalColumns={totalColumns}
                  onToggleOpen={onToggleOpen}
                  onDelete={deleteSelfInsuredRetention}
                  onUpdate={handleRetentionUpdate}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Component>
  );
});

export default ProLinesRetentions;
